import { useEffect, useId, useState, type ComponentProps } from "react";
import { useTranslation, type TFunction } from "react-i18next";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Transition,
} from "motion/react";
import {
  IconChevronDown,
  IconClipboardSearch,
  IconMessageQuestion,
} from "@tabler/icons-react";
import { cn } from "../../lib/utils";
import type { GptAction, GptActionKind } from "../../domains/gpt/transcript";
import type { AssistantDataParts } from "../../../../api/src/shared/assistant-ui-message";
import { actionIcon, TraceGlyph } from "./ChatActionsDropdown";

/**
 * The research while it runs, before the answer's first word, in a bounded
 * number of lines: one row per kind of work, in the order it started. The rows of
 * the calls under way name the action and show the query or title; the other
 * rows count what was done. Nothing is announced before it starts: the writing
 * shows only once the server says the research is over.
 * Folded while the desktop context panel shows the same trace under its
 * Progression: one copy on screen, not two.
 */
export function ChatLiveProgress({
  actions,
  phase,
  isTraceInPanel,
}: {
  actions: ReadonlyArray<
    GptAction & { state: AssistantDataParts["progress"]["state"] }
  >;
  // `searching`: calls run. `writing`: the research is over. `clarifying`: the agent waits on the reader.
  phase: "searching" | "writing" | "clarifying";
  // True once the reader has opened the desktop context panel onto its
  // Progression, false as long as they have not.
  isTraceInPanel: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  // The reader's own fold, kept only for the panel state it was made in: the
  // panel opening or closing folds or unfolds the trace again.
  const [readerFold, setReaderFold] = useState<{
    isTraceInPanel: boolean;
    isOpen: boolean;
  } | null>(null);
  const traceId = useId();
  const elapsed = useElapsedSeconds();
  const questionAction = actions.find((action) => action.kind === "question");
  const reformulation =
    questionAction === undefined ? null : questionAction.query;
  const assumptions = actions.flatMap((action) =>
    action.kind === "assumption" && action.query !== null ? [action.query] : []
  );
  const rows = kindRows(actions);
  const running = latestCall(
    rows.flatMap((row) =>
      row.running === null ? [] : [{ kind: row.kind, call: row.running }]
    )
  );
  const latest = latestCall(
    rows.map((row) => ({ kind: row.kind, call: row.latest }))
  );
  // Between two calls nothing runs: the latest call stands for the research.
  const current = running === null ? latest : running;
  const isSearching = phase === "searching";
  // While the agent waits on the reader, its question card takes the space, so the trace folds to one line.
  const isOpen =
    readerFold !== null && readerFold.isTraceInPanel === isTraceInPanel
      ? readerFold.isOpen
      : !isTraceInPanel && phase !== "clarifying";

  return (
    <div className="flex flex-col gap-2 py-1">
      <div className="flex h-6 items-center justify-between gap-2">
        <Heartbeat isBeating={isSearching}>
          <Label
            text={headline(t, phase)}
            isShimmering={phase === "writing"}
            className="text-ds-slate-900"
          />
        </Heartbeat>
        <button
          type="button"
          onClick={() => {
            setReaderFold({ isTraceInPanel, isOpen: !isOpen });
          }}
          aria-expanded={isOpen}
          aria-controls={traceId}
          aria-label={t("Research details")}
          className="-m-1 flex cursor-pointer items-center gap-2 bg-transparent p-1 font-dm-sans text-footnote text-ds-slate-500"
        >
          {phase === "clarifying" ? null : <LiveDot />}
          <span className="tabular-nums">
            {t("{{count}} s", { count: elapsed })}
          </span>
          <IconChevronDown
            stroke={1.75}
            className={cn(
              "size-4 transition-transform duration-150 ease-out",
              isOpen && "rotate-180"
            )}
          />
        </button>
      </div>
      {isOpen ? (
        <div id={traceId} className="flex flex-col">
          <Row
            icon={{
              kind: "line",
              glyph:
                reformulation === null
                  ? IconClipboardSearch
                  : IconMessageQuestion,
            }}
            label={
              reformulation === null
                ? t("Analyzing your question")
                : t("Question rephrased")
            }
            // A legacy turn sends no reading of the question: its first search ends the analysis.
            isActive={reformulation === null && current === null && isSearching}
            isFirst
          >
            {reformulation === null ? null : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, ease: EASE_OUT }}
                className="flex flex-col"
              >
                <p className="m-0 line-clamp-3 font-dm-sans text-footnote text-ds-slate-900">
                  {reformulation}
                </p>
                {assumptions.map((assumption, index) => (
                  <p
                    key={index}
                    className="m-0 truncate font-dm-sans text-footnote text-ds-slate-500"
                  >
                    {t("Assumption: {{query}}", { query: assumption })}
                  </p>
                ))}
              </motion.div>
            )}
          </Row>
          {rows.map((row) => {
            const isActive =
              isSearching &&
              (running === null
                ? current !== null && current.kind === row.kind
                : row.running !== null);
            const call = row.running === null ? row.latest : row.running;
            return (
              <Row
                key={row.kind}
                icon={actionIcon(row.kind)}
                label={
                  isActive
                    ? activeLabel(t, row.kind)
                    : doneLabel(t, row.kind, row.count)
                }
                isActive={isActive}
                isFirst={false}
              >
                {isActive && call.query !== null ? (
                  <FadeSwap itemKey={String(call.index)} text={call.query} />
                ) : null}
              </Row>
            );
          })}
        </div>
      ) : (
        // Folded: the latest call under way, or once the research is over, what was done.
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, ease: EASE_OUT }}
          className="flex min-w-0 items-center gap-1.25 pl-2 text-ds-slate-500"
        >
          {current === null ? (
            reformulation === null ? (
              <IconClipboardSearch stroke={1.25} className="size-4 shrink-0" />
            ) : (
              <IconMessageQuestion stroke={1.25} className="size-4 shrink-0" />
            )
          ) : (
            <KindIcon kind={current.kind} />
          )}
          <FadeSwap
            itemKey={
              current === null
                ? "question"
                : isSearching
                  ? String(current.call.index)
                  : phase
            }
            text={
              current === null
                ? reformulation === null
                  ? t("Analyzing your question")
                  : t("Question rephrased")
                : isSearching
                  ? [
                      activeLabel(t, current.kind),
                      ...(current.call.query === null
                        ? []
                        : [current.call.query]),
                    ].join(" · ")
                  : rows
                      .map((row) => doneLabel(t, row.kind, row.count))
                      .join(" · ")
            }
          />
        </motion.div>
      )}
    </div>
  );
}

// The calls that the rows count. The reading of the question stays in the first
// row, an answered card is already the reader's bubble, and a suggestion is not research.
type ResearchKind = Exclude<
  GptActionKind,
  "question" | "assumption" | "clarification" | "suggestTopics" | "suggestRooms"
>;

// A call by its position in the trace.
type Call = { index: number; query: string | null };

type KindRow = {
  kind: ResearchKind;
  count: number;
  latest: Call;
  running: Call | null;
};

// One row per kind, in the order the kinds started; each keeps its count, its latest call and its latest running call.
function kindRows(
  actions: ComponentProps<typeof ChatLiveProgress>["actions"]
): KindRow[] {
  return actions.reduce<KindRow[]>((rows, action, index) => {
    const kind = researchKind(action.kind);
    if (kind === null) return rows;
    const known = rows.find((row) => row.kind === kind);
    const call = { index, query: action.query };
    const next = {
      kind,
      count: known === undefined ? 1 : known.count + 1,
      latest: call,
      running:
        action.state === "running"
          ? call
          : known === undefined
            ? null
            : known.running,
    };
    return known === undefined
      ? [...rows, next]
      : rows.map((row) => (row.kind === kind ? next : row));
  }, []);
}

function latestCall(
  calls: ReadonlyArray<{ kind: ResearchKind; call: Call }>
): { kind: ResearchKind; call: Call } | null {
  return calls.reduce<{ kind: ResearchKind; call: Call } | null>(
    (last, item) =>
      last === null || item.call.index > last.call.index ? item : last,
    null
  );
}

function researchKind(kind: GptActionKind): ResearchKind | null {
  switch (kind) {
    case "question":
    case "assumption":
    case "clarification":
    case "suggestTopics":
    case "suggestRooms":
      return null;
    case "search":
    case "web":
    case "pubmed":
    case "similar":
    case "read":
    case "library":
    case "files":
    case "fileRead":
      return kind;
    default:
      throw new Error(kind satisfies never);
  }
}

function headline(
  t: TFunction,
  phase: "searching" | "writing" | "clarifying"
): string {
  switch (phase) {
    case "searching":
      return t("Researching");
    case "writing":
      return t("Writing the answer");
    case "clarifying":
      return t("Waiting for your answer");
    default:
      throw new Error(phase satisfies never);
  }
}

// The call under way.
function activeLabel(t: TFunction, kind: ResearchKind): string {
  switch (kind) {
    case "search":
      return t("Searching Juisci, PubMed and the web");
    case "web":
      return t("Searching the clinical web");
    case "pubmed":
      return t("Searching PubMed");
    case "similar":
      return t("Finding similar articles");
    case "read":
      return t("Reading the article");
    case "library":
      return t("Searching the Juisci library");
    case "files":
      return t("Searching your files");
    case "fileRead":
      return t("Reading your file");
    default:
      throw new Error(kind satisfies never);
  }
}

// The calls of one kind, once another kind runs.
function doneLabel(t: TFunction, kind: ResearchKind, count: number): string {
  switch (kind) {
    case "search":
      return t("{{count}} searches across Juisci, PubMed and the web", {
        count,
      });
    case "web":
      return t("{{count}} clinical web searches", { count });
    case "pubmed":
      return t("{{count}} PubMed searches", { count });
    case "similar":
      return t("{{count}} similar-article searches", { count });
    case "read":
      return t("{{count}} articles read", { count });
    case "library":
      return t("{{count}} Juisci library searches", { count });
    case "files":
      return t("{{count}} file searches", { count });
    case "fileRead":
      return t("{{count}} files read", { count });
    default:
      throw new Error(kind satisfies never);
  }
}

// Whole seconds since the component mounted, ticking once a second.
function useElapsedSeconds(): number {
  const [startedAt] = useState(() => performance.now());
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsed(Math.floor((performance.now() - startedAt) / 1000));
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [startedAt]);
  return elapsed;
}

const EASE_OUT = [0.16, 1, 0.3, 1] as const;

// The highlight is the middle tenth of a gradient two and a half times the text's width.
const SHIMMER_GRADIENT =
  "linear-gradient(90deg, var(--color-ds-black) 40%, var(--color-ds-slate-300) 50%, var(--color-ds-black) 60%)";

function KindIcon({ kind }: { kind: ResearchKind }): React.JSX.Element {
  return (
    <TraceGlyph
      icon={actionIcon(kind)}
      stroke={1.25}
      className="size-4 shrink-0"
    />
  );
}

function Row({
  icon,
  label,
  isActive,
  isFirst,
  children,
}: {
  icon: ComponentProps<typeof TraceGlyph>["icon"];
  label: string;
  isActive: boolean;
  isFirst: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const isReduced = useReducedMotion() === true;
  return (
    // `position` only: a row that gains or loses its detail line moves the rows
    // below it, and scaling them instead would stretch their text. Reduced motion moves them at once.
    <motion.div
      layout={isReduced ? false : "position"}
      initial={{ opacity: 0, y: isReduced ? 0 : 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: EASE_OUT }}
      className="flex flex-col"
    >
      {isFirst ? null : <span className="ml-4 h-2 w-px bg-ds-slate-300" />}
      <div className="grid grid-cols-[auto_1fr] gap-x-1.25 pl-2">
        <TraceGlyph
          icon={icon}
          stroke={1.25}
          className={cn(
            "mt-0.5 size-4 transition-colors duration-300",
            isActive ? "text-ds-black" : "text-ds-slate-500"
          )}
        />
        <Label
          text={label}
          isShimmering={isActive}
          className={isActive ? "text-ds-black" : "text-ds-slate-500"}
        />
        <div className="col-start-2 min-w-0">{children}</div>
      </div>
    </motion.div>
  );
}

// A new text fades in over the old one, so a row turning from action to count does not jump.
function Label({
  text,
  isShimmering,
  className,
}: {
  text: string;
  isShimmering: boolean;
  className: string;
}): React.JSX.Element {
  const isReduced = useReducedMotion() === true;
  return (
    <span className="grid min-w-0 overflow-hidden">
      <AnimatePresence initial={false}>
        <motion.span
          key={text}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: EASE_OUT }}
          className={cn(
            "col-start-1 row-start-1 truncate font-dm-sans text-footnote transition-colors duration-300",
            className
          )}
        >
          {isShimmering && !isReduced ? (
            <motion.span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: SHIMMER_GRADIENT,
                backgroundSize: "250% 100%",
              }}
              initial={{ backgroundPosition: "100% 0%" }}
              animate={{ backgroundPosition: "0% 0%" }}
              transition={{ duration: 1.8, ease: "linear", repeat: Infinity }}
            >
              {text}
            </motion.span>
          ) : (
            text
          )}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

// The call under way: the next one rises in as the previous one fades up and out, in a single line.
function FadeSwap({
  itemKey,
  text,
}: {
  itemKey: string;
  text: string;
}): React.JSX.Element {
  const travel = useReducedMotion() === true ? 0 : 10;
  return (
    <div className="grid min-w-0 overflow-hidden" aria-live="polite">
      <AnimatePresence>
        <motion.span
          key={itemKey}
          initial={{ opacity: 0, y: travel }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -travel }}
          transition={{ duration: 0.45, ease: EASE_OUT }}
          className="col-start-1 row-start-1 truncate font-dm-sans text-footnote text-ds-slate-500"
        >
          {text}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

// The run is alive even between two calls, while the agent reads what it found.
function LiveDot(): React.JSX.Element {
  const isReduced = useReducedMotion() === true;
  return (
    <motion.span
      className="size-1.5 rounded-full bg-ds-mint-600"
      animate={{ opacity: isReduced ? 1 : HEARTBEAT_OPACITY }}
      transition={HEARTBEAT}
    />
  );
}

// The headline beats with the dot while the research runs: the turn is alive though no word shows yet.
function Heartbeat({
  isBeating,
  children,
}: {
  isBeating: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const isReduced = useReducedMotion() === true;
  const beats = isBeating && !isReduced;
  return (
    <motion.span
      className="grid min-w-0"
      animate={{ opacity: beats ? HEARTBEAT_OPACITY : 1 }}
      transition={beats ? HEARTBEAT : { duration: 0.3, ease: EASE_OUT }}
    >
      {children}
    </motion.span>
  );
}

// Two beats, then a rest.
const HEARTBEAT_OPACITY = [1, 0.4, 1, 0.4, 1, 1];
const HEARTBEAT: Transition = {
  duration: 1.4,
  times: [0, 0.1, 0.2, 0.3, 0.45, 1],
  ease: "easeInOut",
  repeat: Infinity,
};
